import type { ReportSummary } from "./api";

// Filters is the canonical set of report filters the dashboard exposes on
// the reports page. Each value, if present, restricts the visible list.
export interface ReportFilters {
  status?: string;
  severity?: string;
  platform?: string;
  gameVersion?: string;
  query?: string;
}

// SortField represents one of the columns the user can sort the report
// table by.
export type ReportSortField = "title" | "reference" | "received" | "severity" | "status";
export type SortDirection = "asc" | "desc";

// SEVERITY_ORDER maps severity values to a sortable rank. Higher rank =
// more severe. The order lets us rank "critical" reports above "low" ones
// when sorting by severity.
const SEVERITY_ORDER: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  unknown: 0,
};

const STATUS_ORDER: Record<string, number> = {
  new: 4,
  triaged: 3,
  in_progress: 2,
  resolved: 1,
  ignored: 0,
};

// filterReports applies the active filter set to a list of report
// summaries. The filter is intentionally permissive: empty strings or
// `undefined` values are treated as no-op, and the platform/version
// comparisons are case-insensitive.
export function filterReports(
  reports: ReportSummary[],
  filters: ReportFilters
): ReportSummary[] {
  const query = filters.query?.trim().toLowerCase();
  return reports.filter((r) => {
    if (filters.status && r.status !== filters.status) return false;
    if (filters.severity && r.severity !== filters.severity) return false;
    if (filters.platform && r.platform.toLowerCase() !== filters.platform.toLowerCase()) {
      return false;
    }
    if (
      filters.gameVersion &&
      r.gameVersion.toLowerCase() !== filters.gameVersion.toLowerCase()
    ) {
      return false;
    }
    if (query) {
      const haystack = `${r.title} ${r.reference} ${r.gameVersion} ${r.platform}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

// sortReports returns a new list sorted by the requested field. Sort is
// stable in the sense that ties fall back to descending received time, which
// matches the natural order of an unsorted report feed.
export function sortReports(
  reports: ReportSummary[],
  field: ReportSortField,
  direction: SortDirection
): ReportSummary[] {
  const factor = direction === "asc" ? 1 : -1;
  const copy = reports.slice();
  copy.sort((a, b) => {
    let primary = 0;
    switch (field) {
      case "title":
        primary = a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
        break;
      case "reference":
        primary = a.reference.localeCompare(b.reference, undefined, { numeric: true });
        break;
      case "received":
        primary = new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime();
        break;
      case "severity":
        primary =
          (SEVERITY_ORDER[a.severity] ?? 0) - (SEVERITY_ORDER[b.severity] ?? 0);
        break;
      case "status":
        primary = (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);
        break;
    }
    if (primary !== 0) return primary * factor;
    // Tiebreaker: most recent first.
    return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
  });
  return copy;
}

// uniqueValues collects the distinct non-empty values for a given field
// across the report list, sorted in case-insensitive ascending order. Used
// to populate platform and version filter chips.
export function uniqueValues(reports: ReportSummary[], field: "platform" | "gameVersion"): string[] {
  const seen = new Map<string, string>();
  for (const r of reports) {
    const value = (r[field] ?? "").trim();
    if (!value) continue;
    const key = value.toLocaleLowerCase();
    if (!seen.has(key)) seen.set(key, value);
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase(), undefined, { sensitivity: "base" })
  );
}

// SEVERITY_RANK / STATUS_OPTIONS expose the canonical filter dropdown
// ordering, so the UI doesn't drift away from the backend values.
export const SEVERITY_OPTIONS = ["critical", "high", "medium", "low", "unknown"] as const;
export const STATUS_OPTIONS = ["new", "triaged", "in_progress", "resolved", "ignored"] as const;
